import React, { type CSSProperties, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import {
  CircleChevronDownIcon,
  CircleChevronUpIcon,
  SettingsIcon as SettingsLucideIcon,
  TriangleAlertIcon,
} from "lucide-react";

import { css, cx, styled } from "simplydo/react/styled";
import { mobileOnly } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import {
  Message,
  MessageContent,
  MessageHeader,
} from "simplydo/react/ui/message";
import { Popup } from "simplydo/react/ui/popup";
import { Progress } from "simplydo/react/ui/progress";
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from "simplydo/react/ui/table";

import api from "api";
import { useCurrentUser, userCache } from "utils/currentUser";

import { LargeCheckbox } from "components/lib/UI";

import ActionDropdown, { type ActionDropdownProps } from "./ActionDropdown";
import DataRow from "./DataRow";
import TableSettings from "./TableSettings";
import type {
  Column,
  ColumnOption,
  ColumnSetting,
  TableRowItem,
} from "./types";
import type { TableSelection } from "./useTableSelection";

const actionKey = "__actions";
const minCellWidth = 75;

export type Columns<ItemType extends TableRowItem> = ColumnOption<ItemType>[];

export type ConfigurableTableProps<ItemType extends TableRowItem> = {
  keyExtractor?: (item: ItemType) => React.Key;
  tableKey: string;
  data: ItemType[];
  columns: Columns<ItemType>;
  onRowClick?: (
    item: ItemType,
    event: React.MouseEvent<HTMLTableRowElement>,
  ) => void;
  getRowStyle?: (item: ItemType) => CSSProperties;
  getCellStyle?: (
    column: Column<ItemType>,
    item: ItemType,
    index: number,
  ) => CSSProperties;
  /** Created with `useTableSelection`. */
  selection?: TableSelection<ItemType>;
  actions?: ActionDropdownProps<ItemType>["actions"];
  sort?: { key: keyof ItemType; direction: "asc" | "desc" };
  onSort?: (key: keyof ItemType, direction: "asc" | "desc") => void;
  stackable?: boolean;
  wrapperStyle?: CSSProperties;
  containerStyle?: CSSProperties;
  basic?: boolean;
  headerStyle?: CSSProperties;
  loading?: boolean;
  progress?: number;
  /** Scrolls the rows under a sticky header once the table is this tall */
  maxHeight?: number | string;
};

const TableWrapper = styled.div`
  margin-top: 0;
  align-self: stretch;
  margin-bottom: 10px;
  position: relative;
  max-width: 100%;
  display: flex;
  flex: 1;
  flex-direction: column;

  ${mobileOnly} {
    margin-top: 10px;
  }
`;

const tableProgressClass = css`
  position: absolute !important;
  z-index: 500;
  width: auto;
  top: 2px;
  left: 2px;
  right: 2px;

  height: 4px;
  margin: 0 !important;

  .bar {
    height: 100% !important;
    border-radius: 3px !important;
    background-color: #2185d0 !important;
  }
`;

const SettingsIcon = styled.button`
  z-index: 3;
  position: absolute;
  right: 2px;
  top: 2px;
  cursor: pointer;
  padding: 0 2px;
  border: none;
  background: none;
  line-height: 1;
  border-radius: 4px;
  color: #767676;

  :hover {
    background: rgba(0, 0, 0, 0.15);
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }
`;

const sizedHeaderCellClass = css`
  min-width: ${minCellWidth}px;

  &&&&.is-sortable:hover {
    cursor: pointer;
    background: #e9e9e9;
  }
`;

const preferredHeaderCellStyle = (width?: number): CSSProperties =>
  width
    ? {
        minWidth: `calc(${width}px + 1.2em)`,
        width: `calc(${width}px + 1.2em)`,
      }
    : { minWidth: minCellWidth };

const defaultKeyExtractor = <ItemType extends TableRowItem>(item: ItemType) =>
  item._id as string;

const ConfigurableTable = <ItemType extends TableRowItem>({
  tableKey,
  data,
  columns,
  onRowClick,
  sort,
  onSort,
  selection,
  actions,
  keyExtractor = selection?.getKey ?? defaultKeyExtractor,
  wrapperStyle,
  containerStyle,
  headerStyle,
  stackable,
  getRowStyle,
  getCellStyle,
  loading,
  progress,
  maxHeight,
}: ConfigurableTableProps<ItemType>) => {
  const { t } = useTranslation();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const user = useCurrentUser();
  const savedColumns = useMemo(() => {
    if (!user) {
      return [];
    }
    const { tablePreferences = {} } = user;
    const { columns: userColumns = [] } = tablePreferences?.[tableKey] ?? {};
    return userColumns ?? [];
  }, [user, tableKey]);

  const columnSettings = columns
    .filter((col) => !!col && (!("isVisible" in col) || !!col.isVisible))
    .map((column, i) => {
      const savedEntry = savedColumns.find((col) => col.key === column.key);
      return {
        ...column,
        name: column.name ?? column.key,
        enabled: savedEntry?.enabled ?? column.enabled ?? true,
        order: savedEntry?.order ?? i,
      };
    })
    .sort((a, b) => a.order - b.order);
  let enabledColumns = columnSettings.filter((col) => col.enabled);

  const hasActions = !!actions;
  if (hasActions) {
    enabledColumns = [
      ...enabledColumns,
      {
        key: actionKey,
        name: "Actions dropdown",
        width: 75,
        center: true,
        enabled: true,
        order: enabledColumns.length,
      },
    ];
  }

  const selectable = !!selection;
  const selectableRows = data.filter((item) => !item.disabled);
  const selectedOnPageCount = selection
    ? selectableRows.filter(selection.isSelected).length
    : 0;
  const allPageSelected =
    selectableRows.length > 0 && selectedOnPageCount === selectableRows.length;
  const somePageSelected = selectedOnPageCount > 0 && !allPageSelected;

  const updateColumns = (newColumns: Column<ItemType>[] | null) => {
    // Filter props to only save the ones we actually need in the db
    const saveableColumns: ColumnSetting[] | null =
      newColumns?.map((col, index) => ({
        key: col.key,
        enabled: col.enabled ?? true,
        order: col.order ?? index,
      })) ?? null;
    const prevSettings = [...savedColumns];

    // Apply immediately locally, but revert to previous if the api call fails
    userCache.updateTablePreferences(tableKey, {
      columns: saveableColumns ?? [],
    });
    api.users.updateTablePreferences(
      user?._id ?? "",
      tableKey,
      { columns: saveableColumns ?? [] },
      () => {},
      () => {
        userCache.updateTablePreferences(tableKey, { columns: prevSettings });
      },
    );
  };

  if (enabledColumns.length === 0) {
    const text =
      data.length > 0
        ? `There are ${data.length} results but all columns are disabled so there is no data to show.`
        : "There are no results and all columns are disabled so there is no data to show.";

    return (
      <>
        <TableSettings
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          updateColumns={updateColumns}
          columns={columnSettings}
        />
        <Message color="orange">
          <Icon icon={TriangleAlertIcon} />
          <MessageHeader>No columns enabled</MessageHeader>
          <MessageContent>
            <p>{text}</p>
            <Button onClick={() => setSettingsOpen(true)}>
              Edit table settings
            </Button>
          </MessageContent>
        </Message>
      </>
    );
  }

  return (
    <TableWrapper style={wrapperStyle} aria-busy={!!loading}>
      <TableSettings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        updateColumns={updateColumns}
        columns={columnSettings}
      />
      <Progress
        className={tableProgressClass}
        style={{ visibility: progress || loading ? "visible" : "hidden" }}
        indicating
        percent={progress ? progress : loading ? 100 : 0}
      />
      <Popup
        trigger={
          <SettingsIcon
            type="button"
            aria-label={t("table.settings")}
            onClick={() => setSettingsOpen(true)}
          >
            <Icon size="small" icon={SettingsLucideIcon} color="grey" />
          </SettingsIcon>
        }
        offset={[8, 0]}
      >
        Edit layout and change settings for this table.
      </Popup>
      <Table
        compact
        style={{ margin: 0, ...containerStyle }}
        stickyLastColumn={hasActions}
        selectable={!!onRowClick}
        stackable={stackable}
        unstackable={!stackable}
        maxHeight={maxHeight}
      >
        <TableHeader style={headerStyle}>
          <TableRow>
            {selection?.selectAll ? (
              <TableHeaderCell
                className={sizedHeaderCellClass}
                style={preferredHeaderCellStyle(40)}
              >
                <div
                  style={{
                    display: "flex",
                    flex: 1,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <LargeCheckbox
                    aria-label={t("table.selectAll")}
                    checked={allPageSelected}
                    indeterminate={somePageSelected}
                    onChange={(e, checked) => {
                      e.preventDefault();
                      if (checked) {
                        selection.select(selectableRows);
                      } else {
                        selection.deselect(
                          selectableRows.map(selection.getKey),
                        );
                      }
                    }}
                  />
                </div>
              </TableHeaderCell>
            ) : selectable ? (
              <TableHeaderCell
                className={sizedHeaderCellClass}
                style={preferredHeaderCellStyle(40)}
              />
            ) : null}
            {enabledColumns.map((col) => (
              <TableHeaderCell
                key={col.key}
                className={cx(
                  sizedHeaderCellClass,
                  col.sortable && onSort && "is-sortable",
                )}
                //Calls the onSort function, with the key
                onClick={() =>
                  col.sortable &&
                  onSort &&
                  onSort(
                    col.key,
                    sort?.key !== col.key
                      ? "asc"
                      : sort.direction === "asc"
                        ? "desc"
                        : "asc",
                  )
                }
                collapsing={col.collapsing}
                style={{
                  ...preferredHeaderCellStyle(col.width),
                  ...(col.style || {}),
                  ...(col.key === actionKey
                    ? { textAlign: "center", width: 85, minWidth: 85 }
                    : {}),
                }}
              >
                {col.key === actionKey ? (
                  selection && (
                    <ActionDropdown
                      actions={actions}
                      items={selection.items}
                      type="multi"
                      user={user}
                      label={t("table.selectedActions")}
                    />
                  )
                ) : (
                  <div
                    style={{
                      display: "inline-flex",
                      flexDirection: "row",
                      flexWrap: "nowrap",
                      alignItems: "center",
                      gap: "0.35em",
                      lineHeight: col?.compactTitle ? "1" : undefined,
                    }}
                  >
                    {col.name}
                    {col.sortable && sort && sort.key === col.key ? (
                      <Icon
                        icon={
                          sort.direction === "asc"
                            ? CircleChevronUpIcon
                            : CircleChevronDownIcon
                        }
                      />
                    ) : null}
                  </div>
                )}
              </TableHeaderCell>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((item, index) => (
            <DataRow
              index={index}
              key={keyExtractor(item)}
              selected={!!selection?.isSelected(item)}
              selectable={selectable}
              item={item}
              enabledColumns={enabledColumns}
              onSelect={selection?.toggle}
              onRowClick={onRowClick}
              actions={typeof actions === "function" ? actions(data) : actions}
              getRowStyle={getRowStyle}
              getCellStyle={
                getCellStyle
                  ? (col, rowItem) => getCellStyle(col, rowItem, index)
                  : undefined
              }
            />
          ))}
        </TableBody>
      </Table>
    </TableWrapper>
  );
};

export default React.memo(ConfigurableTable) as typeof ConfigurableTable;
